import { createContext } from "react";

import type {
  LoginResult,
  PortalId,
  SessionSnapshot,
  TenantSelectionRequired,
  TokenResponse,
} from "@/shared/api/types";

export type AuthStatus = "initialising" | "authenticated" | "anonymous";

export interface OidcCompletion {
  portal: PortalId;
  returnTo: string | null;
}

export interface AuthContextValue {
  status: AuthStatus;
  session: SessionSnapshot | null;
  login: (identifier: string, password: string) => Promise<LoginResult>;
  selectTenant: (loginToken: string, membershipId: string) => Promise<TokenResponse>;
  /** Redirect the browser to a federated provider's authorization endpoint. */
  beginOidcLogin: (providerSlug: string, schoolSlug?: string) => Promise<void>;
  /** Exchange the provider's authorization code for one of our sessions. */
  completeOidcLogin: (code: string, state: string) => Promise<OidcCompletion>;
  /** A federated sign-in that needs a school chosen before it can complete. */
  pendingSelection: TenantSelectionRequired | null;
  clearPendingSelection: () => void;
  /**
   * A federated sign-in that authenticated a platform super admin.
   *
   * They have no tenant and no portal here, so the callback sends them back to
   * the sign-in screen and this says why. The password entrance handles the
   * same case inline, where it already has the result in hand.
   */
  platformSignIn: boolean;
  clearPlatformSignIn: () => void;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

/** Kept in its own module so AuthContext.tsx exports only components (HMR). */
export const AuthContext = createContext<AuthContextValue | null>(null);
